import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useSupabase } from '@/hooks/useSupabase';
import { isLauncherEnabled } from '@/features/launcher/launcherPref';

import { ActivateQuickLoginOffer } from './components/ActivateQuickLoginOffer';
import { LoginCard } from './components/LoginCard';
import { LoginStage } from './components/LoginStage';
import { MfaOverlay } from './components/MfaOverlay';
import { QuickLoginPanel } from './components/QuickLoginPanel';
import { useActivationOffer } from './hooks/useActivationOffer';
import { useEsMovil } from './hooks/useEsMovil';
import { useIntroSequence } from './hooks/useIntroSequence';
import { usePasswordLogin } from './hooks/usePasswordLogin';
import { type LoginValues, loginSchema } from './loginSchema';
import { MENSAJES_DE_FALLO } from './passkeys/api/passkeyLoginService';
import { isPasskeySupported } from './passkeys/api/passkeyService';
import { vePasskeysDeIcloud } from './quick-login/plataforma';
import { useQuickLogin } from './quick-login/useQuickLogin';
import { useRememberedUser } from './quick-login/useRememberedUser';

/**
 * /login: compone el escenario visual, el ingreso con contraseña (con su 2FA en
 * línea) y la entrada rápida con biometría. La lógica de cada flujo vive en sus
 * hooks; acá solo se conectan.
 */
export function LoginPage() {
  const { session, loading } = useSupabase();
  const navigate = useNavigate();
  const location = useLocation();
  const { phase, zoom } = useIntroSequence();
  const isMobile = useEsMovil();

  // `fromState` existe solo si el user fue rebotado a /login desde una ruta
  // protegida (deep link). En ese caso respetamos ese destino. Si no hubo deep
  // link, un LOGIN REAL va al launcher (si está activado); el guard de
  // sesión-restaurada (abajo) usa `from` → Dashboard, nunca launcher.
  const fromState = (location.state as { from?: { pathname: string } } | null)
    ?.from?.pathname;
  const from = fromState ?? '/dashboard';
  const postLoginDest =
    fromState ?? (isLauncherEnabled() ? '/launcher' : '/dashboard');
  const irAlDestino = () => navigate(postLoginDest, { replace: true });

  // Usuario recordado en este navegador (solo el email) y entrada rápida con la
  // biometría. `usarContrasena` es la salida a mano: el formulario de siempre.
  const remembered = useRememberedUser();
  const [usarContrasena, setUsarContrasena] = useState(false);
  const modoRapido = remembered.rapido && isPasskeySupported() && !usarContrasena;
  const quick = useQuickLogin({
    email: remembered.email,
    // Con la sesión viva el login no se muestra: no pedir un desafío al pedo.
    activo: modoRapido && !session,
  });

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: remembered.email, password: '' },
  });

  const oferta = useActivationOffer({ remembered, irAlDestino });
  const password = usePasswordLogin({
    form,
    decidirOferta: oferta.decidir,
    irAlDestino,
    // La marca local, o un dispositivo Apple con Safari: ahí la passkey de iCloud
    // ya está sin activar nada. En Chrome, Edge y Firefox solo con la marca:
    // sin ella abren el QR del celular.
    navegadorTienePasskey: (email) =>
      (remembered.rapido && remembered.email === email.trim().toLowerCase()) ||
      vePasskeysDeIcloud(),
    alPasskeyOk: remembered.activar,
  });

  // IMPORTANTE: este guard NO debe disparar mientras estamos procesando MFA
  // inline (ingresando o modal abierto). Si lo hace, el Navigate manda al
  // dashboard, RequireMFAVerified detecta aal1 con factor enrolled, y
  // redirige a /auth/mfa-challenge — exactamente lo que estamos tratando de
  // evitar con el modal inline. Tampoco mientras se ofrece activar la entrada
  // rápida (es la última parada antes de entrar) ni mientras la biometría de la
  // entrada rápida termina de instalar la sesión.
  if (
    !loading &&
    session &&
    !password.submitting &&
    password.mfaModal.kind === 'closed' &&
    !oferta.abierta &&
    !quick.ingresando
  ) {
    return <Navigate to={from} replace />;
  }

  async function onEntrarRapido() {
    const motivo = await quick.iniciar();
    if (!motivo) {
      remembered.recordar(remembered.email);
      toast.success('Bienvenido');
      irAlDestino();
      return;
    }
    if (motivo === 'sin-credencial') {
      // Este navegador no tiene la passkey (otra PWA, otro perfil, la revocaron):
      // deja de ofrecerse y queda el formulario de contraseña.
      remembered.desactivar();
      toast.error(MENSAJES_DE_FALLO[motivo].titulo, {
        description: MENSAJES_DE_FALLO[motivo].detalle,
      });
    }
  }

  function onNoSoyYo() {
    remembered.noSoyYo();
    setUsarContrasena(false);
    form.reset({ email: '', password: '' });
    // El campo todavía no existe si veníamos del panel de entrada rápida: se
    // enfoca cuando React ya lo montó.
    setTimeout(() => form.setFocus('email'), 0);
  }

  return (
    <LoginStage
      isMobile={isMobile}
      phase={phase}
      zoom={zoom}
      tarjeta={({ visible, isMobile: movil }) => (
        <LoginCard
          form={form}
          onSubmit={password.onSubmit}
          submitting={password.submitting}
          recordado={remembered.email !== ''}
          onNoSoyYo={onNoSoyYo}
          quickSlot={
            modoRapido ? (
              <QuickLoginPanel
                email={remembered.email}
                preparando={quick.preparando}
                ingresando={quick.ingresando}
                fallo={quick.fallo}
                onEntrar={() => void onEntrarRapido()}
                onUsarContrasena={() => setUsarContrasena(true)}
                onNoSoyYo={onNoSoyYo}
              />
            ) : undefined
          }
          visible={visible}
          isMobile={movil}
        />
      )}
    >
      {oferta.abierta && (
        <ActivateQuickLoginOffer
          onActivada={() => oferta.onActivada(form.getValues('email'))}
          onAhoraNo={oferta.onAhoraNo}
        />
      )}

      {password.mfaModal.kind !== 'closed' && (
        <MfaOverlay
          state={password.mfaModal}
          totpCode={password.totpCode}
          setTotpCode={password.setTotpCode}
          totpSubmitting={password.totpSubmitting}
          onTotpSubmit={password.onTotpSubmit}
          onCancel={password.onCancelMfa}
          onUsarPasskey={() => void password.usarPasskey()}
        />
      )}
    </LoginStage>
  );
}
